/*
 * A widget shown for show-and-tell at the card's own size: the same host a board uses, fed made-up
 * readings that keep coming, so a sparkline draws, a spectrum finds its peak
 * and a box plot fills — with no gateway behind it. Each series is a sine of a
 * given period plus a little noise around a base; a pair with the same period
 * and a phase offset correlates, which is what a scatter or a lens wants.
 *
 * Marketplace previews and nothing else use it. The readings are keyed by
 * symbolId, which is all the sandbox reads; the controllerId in the config is
 * a placeholder the person replaces when they bind the installed tile.
 */
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TypedValue } from '../core/bridge/connect-client';
import { DENY_ALL, type CapabilityGrant } from './runtime/capability';
import './widget-host';

export interface DemoSeries {
  symbolId: string;
  /** The value the series moves around. */
  base: number;
  /** How far the sine swings. */
  amp: number;
  /** Seconds per cycle. */
  period: number;
  /** Noise amplitude (uniform). */
  noise?: number;
  /** Phase offset in cycles (0 – 1). */
  phase?: number;
  /** A second tone on top of the first, for a signal with more than one period in it. */
  amp2?: number;
  period2?: number;
  /** A random walk of this step per reading, so relations between series drift instead of freezing. */
  walk?: number;
  /** Added over the last `shiftSec` of history and to every live reading: a recent move to explain. */
  shift?: number;
  shiftSec?: number;
}

/** A preview may read nothing and never expires: DENY_ALL's zero expiry would fault it at once. */
const PREVIEW_GRANT: CapabilityGrant = { ...DENY_ALL, expiresAtMs: null };

@customElement('vitesse-widget-demo')
export class VitesseWidgetDemo extends LitElement {
  @property({ attribute: false }) source = '';
  @property({ attribute: false }) config: Record<string, unknown> = {};
  @property({ attribute: false }) series: DemoSeries[] = [];
  /** Readings per second. */
  @property({ type: Number }) rate = 2;
  /** Seconds of made-up history replayed through the widget before the live readings. */
  @property({ type: Number }) historySec = 120;

  @state() private values: TypedValue[] = [];
  private timer = 0;
  private readonly t0 = Date.now();
  private past: TypedValue[] = [];
  private readonly walks = new Map<string, number>();

  createRenderRoot(): HTMLElement { return this; }

  connectedCallback(): void {
    super.connectedCallback();
    this.style.display = 'block';
    this.style.height = '100%';
    this.past = this.history();
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000 / Math.max(0.2, this.rate));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    clearInterval(this.timer);
  }

  private sample(s: DemoSeries, atMs: number, now: number): number {
    const t = (atMs - this.t0) / 1000;
    const recent = now - atMs <= (s.shiftSec ?? 10) * 1000;
    let walk = this.walks.get(s.symbolId) ?? 0;
    if (s.walk) { walk = Math.max(-s.amp * 2, Math.min(s.amp * 2, walk + s.walk * (Math.random() * 2 - 1))); this.walks.set(s.symbolId, walk); }
    return walk + s.base + s.amp * Math.sin((2 * Math.PI * (t / s.period + (s.phase ?? 0)))) + (s.amp2 && s.period2 ? s.amp2 * Math.sin((2 * Math.PI * t) / s.period2) : 0) + (s.noise ?? 0) * (Math.random() * 2 - 1) + (recent ? (s.shift ?? 0) : 0);
  }

  /** The history the widget starts with: every series sampled at the live rate across `historySec`. */
  private history(): TypedValue[] {
    const now = Date.now();
    const step = 1000 / Math.max(0.2, this.rate);
    const out: TypedValue[] = [];
    this.walks.clear();
    for (let at = now - this.historySec * 1000; at < now; at += step) {
      const ts = Math.round(at);
      for (const s of this.series) { const v = this.sample(s, ts, now); out.push({ symbolId: s.symbolId, value: v, num: v, tsMs: ts }); }
    }
    return out;
  }

  private tick(): void {
    const now = Date.now();
    this.values = this.series.map((s) => { const v = this.sample(s, now, now); return { symbolId: s.symbolId, value: v, num: v, tsMs: now }; });
  }

  render() {
    return html`<vitesse-widget-host style="display:block;height:100%" .source=${this.source} .config=${this.config} .previewHistory=${this.past} .previewValues=${this.values} .grant=${PREVIEW_GRANT}></vitesse-widget-host>`;
  }
}
